export const WORKOUT_RESUME_KEY = "gym.workoutResume.v1";
export const WORKOUT_RESUME_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type WorkoutResumePosition = {
  version: 1;
  workoutId: string;
  exerciseId: string | null;
  exerciseOffsetPx: number | null;
  scrollY: number;
  savedAt: number;
};

type ResumeStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function readWorkoutResumePosition(
  storage: ResumeStorage,
  workoutId: string,
  now = Date.now()
): WorkoutResumePosition | null {
  try {
    const raw = storage.getItem(WORKOUT_RESUME_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as unknown;
    if (!isWorkoutResumePosition(value) || value.workoutId !== workoutId) return null;
    if (value.savedAt > now + 60_000 || now - value.savedAt > WORKOUT_RESUME_MAX_AGE_MS) return null;
    return value;
  } catch {
    return null;
  }
}

export function writeWorkoutResumePosition(storage: ResumeStorage, position: WorkoutResumePosition): void {
  try {
    storage.setItem(WORKOUT_RESUME_KEY, JSON.stringify(position));
  } catch {
    // Position recovery is best-effort and must never interrupt a workout.
  }
}

export function clearWorkoutResumePosition(storage: ResumeStorage, workoutId: string): void {
  try {
    const raw = storage.getItem(WORKOUT_RESUME_KEY);
    if (!raw) return;
    const value = JSON.parse(raw) as unknown;
    if (isWorkoutResumePosition(value) && value.workoutId === workoutId) storage.removeItem(WORKOUT_RESUME_KEY);
  } catch {
    try {
      storage.removeItem(WORKOUT_RESUME_KEY);
    } catch {
      // Position recovery is best-effort and must never interrupt a workout.
    }
  }
}

export function calculateRestoredScrollY(
  position: WorkoutResumePosition,
  currentScrollY: number,
  currentExerciseTop: number | null,
  maximumScrollY: number
): number {
  const anchoredPosition = currentExerciseTop !== null && position.exerciseOffsetPx !== null
    ? currentScrollY + currentExerciseTop - position.exerciseOffsetPx
    : position.scrollY;
  return Math.min(Math.max(0, anchoredPosition), Math.max(0, maximumScrollY));
}

function isWorkoutResumePosition(value: unknown): value is WorkoutResumePosition {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<WorkoutResumePosition>;
  return candidate.version === 1
    && typeof candidate.workoutId === "string"
    && candidate.workoutId.length > 0
    && (candidate.exerciseId === null || (typeof candidate.exerciseId === "string" && candidate.exerciseId.length > 0))
    && (candidate.exerciseOffsetPx === null || isFiniteNumber(candidate.exerciseOffsetPx))
    && isFiniteNumber(candidate.scrollY)
    && candidate.scrollY >= 0
    && isFiniteNumber(candidate.savedAt)
    && candidate.savedAt >= 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}
